export type DebouncedFunction<T extends (...args: never[]) => void> = ((...args: Parameters<T>) => void) & {
  cancel: () => void
}

/**
 * Returns a debounced function that delays invoking `fn` until after `delayMs` has elapsed
 * since the last call. The returned function has a `cancel()` method to clear pending invocations.
 */
export function debounce<T extends (...args: never[]) => void>(
  fn: T,
  delayMs: number | (() => number),
): DebouncedFunction<T> {
  let timeoutId: ReturnType<typeof setTimeout> | undefined

  const debounced = (...args: Parameters<T>) => {
    if (timeoutId !== undefined) {
      clearTimeout(timeoutId)
    }

    const delay = typeof delayMs === 'function' ? delayMs() : delayMs
    timeoutId = setTimeout(() => {
      timeoutId = undefined
      fn(...args)
    }, delay)
  }

  debounced.cancel = () => {
    if (timeoutId !== undefined) {
      clearTimeout(timeoutId)
      timeoutId = undefined
    }
  }

  return debounced
}
